import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: String(currency || "usd").toUpperCase() }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function CustomerCheckoutForm({ ticketNum, customerToken, amountDueCents, currency, paymentIntentId, onPaid }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function handleSubmit(evt) {
        evt.preventDefault();
        if (!stripe || !elements || isSubmitting) return;

        try {
            setIsSubmitting(true);
            const returnUrl = typeof window !== "undefined" ? window.location.href : undefined;
            const result = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
                confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
            });

            if (result.error) {
                toast.error(result.error.message || "Payment failed", { autoClose: 1500 });
                return;
            }

            const confirmedIntentId = result?.paymentIntent?.id || paymentIntentId;
            if (!confirmedIntentId) {
                toast.error("Payment confirmed but no intent ID was returned", { autoClose: 1500 });
                return;
            }

            await ValetApi.customerSyncPaymentIntent(ticketNum, customerToken, confirmedIntentId);
            toast.success("Payment complete", { autoClose: 1200 });
            onPaid();
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to process payment", { autoClose: 1500 });
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="cvi-payment-line">Amount due now: {formatCents(amountDueCents, currency)}</div>
            <PaymentElement options={{ layout: "tabs" }} />
            <Button
                color="success"
                type="submit"
                className="cvi-pay-submit"
                disabled={!stripe || !elements || isSubmitting}>
                {isSubmitting ? "Processing..." : "Pay Now"}
            </Button>
        </form>
    );
}

const CustomerPaymentPanel = ({ ticketNum, customerToken, vehicleStatusId, onStatusUpdate }) => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
    const stripePromise = useMemo(() => {
        if (!publishableKey) return null;
        return loadStripe(publishableKey);
    }, [publishableKey]);

    const [paymentStatus, setPaymentStatus] = useState(null);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
    const [isPreparingIntent, setIsPreparingIntent] = useState(false);
    const [intentData, setIntentData] = useState(null);
    const [showCheckout, setShowCheckout] = useState(false);

    const currentStatusId = Number(vehicleStatusId || 0);
    const canPayForCurrentStatus = [1, 2, 3, 4, 5].includes(currentStatusId);

    async function refreshStatus() {
        if (!ticketNum || !customerToken) {
            setPaymentStatus(null);
            if (typeof onStatusUpdate === "function") onStatusUpdate(null);
            setHasLoadedStatus(true);
            return;
        }

        try {
            setIsLoadingStatus(true);
            const res = await ValetApi.customerGetVehiclePaymentStatus(ticketNum, customerToken);
            const nextStatus = res?.data?.status || null;
            setPaymentStatus(nextStatus);
            if (typeof onStatusUpdate === "function") onStatusUpdate(nextStatus);
        } catch (err) {
            setPaymentStatus(null);
            if (typeof onStatusUpdate === "function") onStatusUpdate(null);
            console.error(err);
        } finally {
            setIsLoadingStatus(false);
            setHasLoadedStatus(true);
        }
    }

    useEffect(() => {
        refreshStatus();
    }, [ticketNum, customerToken]);

    async function handlePrepareCheckout() {
        if (!canPayForCurrentStatus) {
            toast.info("Payment is unavailable after checkout", { autoClose: 1200 });
            return;
        }

        try {
            setIsPreparingIntent(true);
            const res = await ValetApi.customerCreatePaymentIntent(ticketNum, customerToken);
            const paymentIntent = res?.data?.paymentIntent || null;
            setIntentData(paymentIntent);
            setShowCheckout(true);
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to start payment", { autoClose: 1500 });
            setShowCheckout(false);
        } finally {
            setIsPreparingIntent(false);
        }
    }

    const amountDueCents = Number(paymentStatus?.amountDueCents || 0);
    const paymentModel = paymentStatus?.paymentModel || null;
    const isPaid = Boolean(paymentStatus?.isPaid || paymentStatus?.paymentStatus === "paid");
    const isPaymentFeatureEnabled = Boolean(paymentStatus?.featureEnabled === true);
    const isPodiumPaymentEnabled = Boolean(paymentStatus?.config?.isEnabled);
    const isRequiredForRequest = Boolean(paymentStatus?.config?.isEnabled) && Boolean(paymentStatus?.config?.requirePaymentBeforeRequest) && Boolean(paymentStatus?.requiresPayment);
    const shouldHidePaymentPanel = hasLoadedStatus && (!isPaymentFeatureEnabled || !isPodiumPaymentEnabled || amountDueCents <= 0);

    if (!hasLoadedStatus || shouldHidePaymentPanel) {
        return null;
    }

    return (
        <div className="cvi-payment-card">
            <div className="cvi-payment-header">
                <h3>Parking Payment</h3>
                <Button
                    color="secondary"
                    size="sm"
                    onClick={async () => {
                        await refreshStatus();
                        if (typeof onStatusUpdate === "function") onStatusUpdate();
                    }}>
                    Refresh
                </Button>
            </div>

            {isLoadingStatus ? <Spinner size="sm" /> : null}

            {!isLoadingStatus ? (
                <>
                    <div className="cvi-payment-line">
                        Status: <strong>{!isPaymentFeatureEnabled ? "Disabled" : isPaid ? "Paid" : paymentStatus?.paymentStatus === "not_required" ? "Not Required" : "Pending"}</strong>
                    </div>
                    {!isPaymentFeatureEnabled ? <div className="cvi-payment-warning">Payments are disabled by backend configuration for this environment.</div> : null}
                    <div className="cvi-payment-line">
                        Amount due: <strong>{formatCents(amountDueCents, paymentStatus?.currency || "usd")}</strong>
                    </div>
                    {paymentModel ? <div className="cvi-payment-line">Pricing: {paymentModel.replace("_", " ")}</div> : null}

                    {isPaymentFeatureEnabled && paymentStatus?.requiresPayment && !isPaid && canPayForCurrentStatus ? (
                        <>
                            <div className="cvi-payment-note">{paymentModel === "time_increment" || paymentModel === "time_table" ? "Time-based pricing may continue to accrue until exit or final retrieval." : "You can pay now or at request time."}</div>
                            <Button
                                color="success"
                                onClick={handlePrepareCheckout}
                                disabled={isPreparingIntent}>
                                {isPreparingIntent ? "Preparing..." : "Pay Parking Charges"}
                            </Button>
                        </>
                    ) : null}

                    {isPaymentFeatureEnabled && isRequiredForRequest && !isPaid ? <div className="cvi-payment-warning">Payment is required before request.</div> : null}
                </>
            ) : null}

            <Modal
                isOpen={Boolean(isPaymentFeatureEnabled && showCheckout && intentData?.clientSecret && publishableKey)}
                toggle={() => setShowCheckout(false)}
                centered
                backdrop="static"
                modalClassName="cvi-payment-modal-theme">
                <ModalHeader toggle={() => setShowCheckout(false)}>Secure Payment Checkout</ModalHeader>
                <ModalBody>
                    <div className="cvi-payment-line">Ticket: {ticketNum}</div>
                    <Elements
                        stripe={stripePromise}
                        options={{ clientSecret: intentData?.clientSecret }}>
                        <CustomerCheckoutForm
                            ticketNum={ticketNum}
                            customerToken={customerToken}
                            amountDueCents={Number(intentData?.amountDueCents || 0)}
                            currency={intentData?.currency || "usd"}
                            paymentIntentId={intentData?.stripePaymentIntentId || null}
                            onPaid={async () => {
                                setShowCheckout(false);
                                await refreshStatus();
                            }}
                        />
                    </Elements>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        size="sm"
                        type="button"
                        onClick={() => setShowCheckout(false)}>
                        Close Payment Window
                    </Button>
                </ModalFooter>
            </Modal>

            {isPaymentFeatureEnabled && showCheckout && !publishableKey ? <div className="cvi-payment-warning">Payment is temporarily unavailable. Please see valet for in-person payment.</div> : null}
        </div>
    );
};

export default CustomerPaymentPanel;
